/**
 * Everything the app needs, wired once at startup.
 *
 * Kept out of the React tree so the composition is inspectable and testable on its own —
 * and so there is exactly one place that decides whether this build has a server to talk
 * to. Getting that decision wrong in two places is how a UI ends up showing a spinner for a
 * sync that was never going to happen.
 */

import { OutboxDrainer, uuidv7, type Uuid } from '@rush/sync-core';

import { openRushDatabase, type RushDatabase } from './db/database.js';
import { FieldRepository, type FieldIdentity } from './db/repository.js';
import { SyncEngine } from './sync/engine.js';
import { HttpSyncTransport, resolveApiBaseUrl } from './sync/transport.js';

/**
 * The placeholder identity, explicitly scoped by RUS-8 ("real authentication — a
 * placeholder/dev user is fine for this pass").
 *
 * Fixed UUIDs rather than generated ones so that a rep who reinstalls the app is still the
 * same actor to the server, and so the ids in a bug report are recognisable. `orgId` is
 * real infrastructure even in a single-org v1 (ADR-0001 §6.4) — every op carries it, and
 * retrofitting tenancy later is a full-table migration.
 *
 * These are development constants. They are not credentials, they authorise nothing, and
 * the standing rule against real customer data (ADR-0001 §10) holds until authentication
 * and an approved storage plan exist.
 */
export const DEV_ORG_ID: Uuid = '01920000-0000-7000-8000-000000000001';
export const DEV_ACTOR_ID: Uuid = '01920000-0000-7000-8000-000000000002';

const DEVICE_ID_KEY = 'rush.deviceId';

/**
 * A stable id for this install.
 *
 * Persisted rather than minted per session because it is stamped onto every op as
 * provenance — "which phone captured this" is a question an insurance dispute can turn on,
 * and a device id that changes on refresh answers it wrong. Storage failures (private mode,
 * a full disk) degrade to a session-scoped id instead of blocking capture: a rep on a roof
 * does not get stopped by a bookkeeping problem.
 */
export function resolveDeviceId(storage: Pick<Storage, 'getItem' | 'setItem'> | undefined): Uuid {
  if (storage === undefined) return uuidv7();

  try {
    const existing = storage.getItem(DEVICE_ID_KEY);
    if (existing !== null && existing.length > 0) return existing;

    const minted = uuidv7();
    storage.setItem(DEVICE_ID_KEY, minted);
    return minted;
  } catch {
    return uuidv7();
  }
}

export interface FieldApp {
  readonly db: RushDatabase;
  readonly repository: FieldRepository;
  readonly sync: SyncEngine;
  readonly identity: FieldIdentity;
  /** Absent when this build has no API to sync to. The banner says so. */
  readonly apiBaseUrl: string | undefined;
}

export interface CreateFieldAppOptions {
  readonly env: Readonly<Record<string, string | undefined>>;
  readonly storage?: Pick<Storage, 'getItem' | 'setItem'> | undefined;
  readonly isOnline: () => boolean;
  readonly databaseName?: string;
}

export function createFieldApp(options: CreateFieldAppOptions): FieldApp {
  const identity: FieldIdentity = {
    orgId: DEV_ORG_ID,
    actorId: DEV_ACTOR_ID,
    deviceId: resolveDeviceId(options.storage),
  };

  const { db, outbox, blobs } = openRushDatabase(options.databaseName ?? 'rush-field');
  const repository = new FieldRepository({ db, outbox, blobs, identity, nextId: uuidv7 });

  // No base URL means no drainer at all, rather than a drainer pointed at a placeholder.
  // ADR-0002 §2 deferred API hosting; until it lands, the honest behaviour is to queue and
  // say so, not to retry against a URL nobody ever stood up.
  const apiBaseUrl = resolveApiBaseUrl(options.env);
  const drainer =
    apiBaseUrl === undefined
      ? undefined
      : new OutboxDrainer({
          store: outbox,
          transport: new HttpSyncTransport({ baseUrl: apiBaseUrl }),
        });

  const sync = new SyncEngine({ repository, drainer, isOnline: options.isOnline });

  return { db, repository, sync, identity, apiBaseUrl };
}
